import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  ShoppingCart,
  Package,
  Minus,
  Plus,
  ChevronLeft,
  ChevronRight,
  Truck,
  Shield,
  RotateCcw,
  CheckCircle,
  Building2,
  User,
  Heart,
  Share2,
  ZoomIn
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetail() {
  const { t } = useTranslation();
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [cart, setCart] = useState([]);
  const [imageZoom, setImageZoom] = useState(false);

  useEffect(() => {
    fetchProduct();
    loadCart();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      const res = await axios.get(`${API}/shop/products/public`);
      const found = res.data.find(p => p.product_id === productId);
      if (found) {
        setProduct(found);
      } else {
        toast.error("Product not found");
        navigate("/webshop");
      }
    } catch (err) {
      console.error("Error fetching product:", err);
      toast.error("Error loading product");
    } finally {
      setLoading(false);
    }
  };

  const loadCart = () => {
    const savedCart = localStorage.getItem("allstillas_cart");
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  };

  const saveCart = (newCart) => {
    localStorage.setItem("allstillas_cart", JSON.stringify(newCart));
    setCart(newCart);
  };

  const addToCart = () => {
    // Check if size is required (clothing or footwear)
    const needsSize = (product.categories?.includes("clothing") || product.categories?.includes("footwear") || product.category === "clothing") && product.sizes?.length > 0;
    if (needsSize && !selectedSize) {
      toast.error("Please select a size");
      return;
    }
    
    // Check if variant is required
    const hasVariants = product.variants && product.variants.length > 0;
    if (hasVariants && !selectedVariant) {
      toast.error("Please select a variant");
      return;
    }

    let finalPrice = getDiscountedPrice() || product.price;
    
    // Add variant price adjustment if applicable
    if (selectedVariant && selectedVariant.price_adjustment) {
      finalPrice += parseFloat(selectedVariant.price_adjustment);
    }
    
    const cartItem = {
      product_id: product.product_id,
      name: product.name,
      price: finalPrice,
      photo: product.photos?.[0] || null,
      size: selectedSize,
      variant: selectedVariant?.name || null,
      quantity: quantity
    };

    const existingIndex = cart.findIndex(
      item => item.product_id === product.product_id && 
              item.size === selectedSize && 
              item.variant === (selectedVariant?.name || null)
    );

    let newCart;
    if (existingIndex >= 0) {
      newCart = [...cart];
      newCart[existingIndex].quantity += quantity;
    } else {
      newCart = [...cart, cartItem];
    }

    saveCart(newCart);
    const variantText = selectedVariant ? ` (${selectedVariant.name})` : '';
    const sizeText = selectedSize ? ` - Size ${selectedSize}` : '';
    toast.success(`${product.name}${variantText}${sizeText} added to cart!`);
  };

  const getDiscountedPrice = () => {
    if (!product?.discount_enabled || !product?.discount_percent) return null;
    
    const now = new Date();
    if (!product.discount_permanent) {
      if (product.discount_start_date && new Date(product.discount_start_date) > now) return null;
      if (product.discount_end_date && new Date(product.discount_end_date) < now) return null;
    }
    
    return product.price * (1 - product.discount_percent / 100);
  };

  const nextImage = () => {
    if (product?.photos?.length > 1) {
      setSelectedImage((prev) => (prev + 1) % product.photos.length);
    }
  };

  const prevImage = () => {
    if (product?.photos?.length > 1) {
      setSelectedImage((prev) => (prev - 1 + product.photos.length) % product.photos.length);
    }
  };

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const discountedPrice = product ? getDiscountedPrice() : null;
  // Calculate ex VAT from discounted price if discount exists, otherwise from original
  const displayPrice = discountedPrice || (product ? product.price : 0);
  const priceExVat = displayPrice / 1.25;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 mx-auto text-[#475569] mb-4" />
          <p className="text-white text-xl">Product not found</p>
          <Link to="/webshop">
            <Button className="mt-4 bg-green-500 text-white">Back to Shop</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#050505]/95 backdrop-blur border-b border-white/10 pt-[env(safe-area-inset-top,0px)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/webshop" className="flex items-center gap-2 text-[#94A3B8] hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Shop</span>
            </Link>
            
            <Link to="/cart" className="relative" data-testid="cart-button">
              <Button className="bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 gap-2">
                <ShoppingCart className="w-5 h-5" />
                <span className="hidden sm:inline">Cart</span>
                {cartCount > 0 && (
                  <Badge className="bg-white text-green-600 ml-1">{cartCount}</Badge>
                )}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Image Gallery */}
          <div className="space-y-4">
            {/* Main Image */}
            <div 
              className="relative aspect-square bg-[#0A0A0A] rounded-2xl overflow-hidden border border-white/10 group cursor-zoom-in"
              onClick={() => setImageZoom(true)}
            >
              {product.photos && product.photos.length > 0 ? (
                <img
                  src={product.photos[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Package className="w-32 h-32 text-[#475569]" />
                </div>
              )}
              
              {/* Discount Badge */}
              {product.discount_enabled && product.discount_percent > 0 && (
                <Badge className="absolute top-4 left-4 bg-red-500 text-white text-lg px-3 py-1">
                  -{product.discount_percent}%
                </Badge>
              )}
              
              {/* Zoom hint */}
              <div className="absolute bottom-4 right-4 bg-black/50 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <ZoomIn className="w-5 h-5 text-white" />
              </div>
              
              {/* Navigation arrows */}
              {product.photos && product.photos.length > 1 && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); prevImage(); }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); nextImage(); }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}
            </div>
            
            {/* Thumbnails */}
            {product.photos && product.photos.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {product.photos.map((photo, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === index 
                        ? "border-green-500 ring-2 ring-green-500/30" 
                        : "border-white/10 hover:border-white/30"
                    }`}
                  >
                    <img
                      src={photo}
                      alt={`${product.name} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            {/* Category */}
            <Badge variant="outline" className="border-green-500/50 text-green-400">
              {product.category?.replace("_", " ").toUpperCase()}
            </Badge>
            
            {/* Title */}
            <h1 className="text-3xl lg:text-4xl font-['Unbounded'] font-bold text-white">
              {product.name}
            </h1>
            
            {/* Price Section */}
            <div className="space-y-2">
              <div className="flex items-baseline gap-3">
                {discountedPrice ? (
                  <>
                    <span className="text-4xl font-bold text-green-400">
                      {discountedPrice.toFixed(0)} NOK
                    </span>
                    <span className="text-2xl text-red-400 line-through">
                      {product.price} NOK
                    </span>
                    <Badge className="bg-red-500 text-white">
                      Save {(product.price - discountedPrice).toFixed(0)} NOK
                    </Badge>
                  </>
                ) : (
                  <span className="text-4xl font-bold text-green-400">
                    {product.price} NOK
                  </span>
                )}
              </div>
              
              {/* VAT Info */}
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1 text-[#94A3B8]">
                  <User className="w-4 h-4" />
                  <span>Price inc. VAT (private)</span>
                </div>
                <div className="flex items-center gap-1 text-[#64748B]">
                  <Building2 className="w-4 h-4" />
                  <span>Ex. VAT: {priceExVat.toFixed(0)} NOK (B2B)</span>
                </div>
              </div>
            </div>
            
            {/* Stock Status */}
            <div className="flex items-center gap-2">
              {product.stock_quantity > 10 ? (
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  In Stock ({product.stock_quantity} available)
                </Badge>
              ) : product.stock_quantity > 0 ? (
                <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">
                  Limited Stock ({product.stock_quantity} left)
                </Badge>
              ) : (
                <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                  Out of Stock
                </Badge>
              )}
            </div>
            
            {/* Description */}
            {product.description && (
              <div className="prose prose-invert max-w-none">
                <p className="text-[#94A3B8] leading-relaxed">{product.description}</p>
              </div>
            )}
            
            {/* Size Selector with Stock - Clothing */}
            {(product.categories?.includes("clothing") || product.category === "clothing") && product.sizes && product.sizes.length > 0 && (
              <div className="space-y-3">
                <label className="text-white font-medium">Select size:</label>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map(size => {
                    const sizeStock = product.size_inventory?.[size] ?? product.stock_quantity;
                    const isOutOfStock = sizeStock <= 0;
                    
                    return (
                      <button
                        key={size}
                        onClick={() => !isOutOfStock && setSelectedSize(size)}
                        disabled={isOutOfStock}
                        className={`px-4 py-2 rounded-lg border-2 transition-all relative ${
                          selectedSize === size
                            ? "bg-green-500 border-green-500 text-white"
                            : isOutOfStock
                            ? "border-white/10 text-[#64748B] cursor-not-allowed line-through"
                            : "border-white/20 text-[#94A3B8] hover:border-green-500/50"
                        }`}
                        data-testid={`size-${size}`}
                      >
                        {size}
                        {!isOutOfStock && sizeStock <= 5 && (
                          <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                            {sizeStock}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {selectedSize && product.size_inventory?.[selectedSize] && (
                  <p className="text-sm text-[#94A3B8]">
                    {product.size_inventory[selectedSize]} items in stock for size {selectedSize}
                  </p>
                )}
              </div>
            )}
            
            {/* Size Selector - Footwear */}
            {product.categories?.includes("footwear") && product.sizes && product.sizes.length > 0 && (
              <div className="space-y-3">
                <label className="text-white font-medium">Select shoe size (EU):</label>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map(size => {
                    const sizeStock = product.size_inventory?.[size] ?? product.stock_quantity;
                    const isOutOfStock = sizeStock <= 0;
                    
                    return (
                      <button
                        key={size}
                        onClick={() => !isOutOfStock && setSelectedSize(size)}
                        disabled={isOutOfStock}
                        className={`w-12 h-12 rounded-lg border-2 transition-all relative ${
                          selectedSize === size
                            ? "bg-orange-500 border-orange-500 text-white font-bold"
                            : isOutOfStock
                            ? "border-white/10 text-[#64748B] cursor-not-allowed line-through"
                            : "border-white/20 text-[#94A3B8] hover:border-orange-500/50"
                        }`}
                        data-testid={`shoe-size-${size}`}
                      >
                        {size}
                        {!isOutOfStock && sizeStock <= 3 && (
                          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] w-4 h-4 flex items-center justify-center rounded-full">
                            {sizeStock}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {selectedSize && product.size_inventory?.[selectedSize] && (
                  <p className="text-sm text-[#94A3B8]">
                    {product.size_inventory[selectedSize]} pairs in stock for size {selectedSize}
                  </p>
                )}
              </div>
            )}
            
            {/* Variant Selector (60cm, 80cm, 1M, etc.) */}
            {product.variants && product.variants.length > 0 && (
              <div className="space-y-3">
                <label className="text-white font-medium">Select variant:</label>
                <div className="flex flex-wrap gap-2">
                  {product.variants.map((variant, idx) => {
                    const isOutOfStock = variant.stock <= 0;
                    const priceAdjustment = parseFloat(variant.price_adjustment) || 0;
                    
                    return (
                      <button
                        key={idx}
                        onClick={() => !isOutOfStock && setSelectedVariant(variant)}
                        disabled={isOutOfStock}
                        className={`px-4 py-3 rounded-lg border-2 transition-all relative flex flex-col items-center min-w-[80px] ${
                          selectedVariant?.name === variant.name
                            ? "bg-[#23F7DD] border-[#23F7DD] text-black"
                            : isOutOfStock
                            ? "border-white/10 text-[#64748B] cursor-not-allowed"
                            : "border-white/20 text-white hover:border-[#23F7DD]/50"
                        }`}
                        data-testid={`variant-${variant.name}`}
                      >
                        <span className={`font-bold ${isOutOfStock ? 'line-through' : ''}`}>{variant.name}</span>
                        {priceAdjustment !== 0 && (
                          <span className={`text-xs mt-1 ${selectedVariant?.name === variant.name ? 'text-black/70' : 'text-[#94A3B8]'}`}>
                            {priceAdjustment > 0 ? '+' : ''}{Math.round(priceAdjustment)} NOK
                          </span>
                        )}
                        <span className={`text-xs mt-1 ${selectedVariant?.name === variant.name ? 'text-black/70' : 'text-[#64748B]'}`}>
                          {variant.stock} stk
                        </span>
                      </button>
                    );
                  })}
                </div>
                {selectedVariant && (
                  <p className="text-sm text-[#94A3B8]">
                    Selected: <span className="text-[#23F7DD] font-medium">{selectedVariant.name}</span> - {selectedVariant.stock} items in stock
                  </p>
                )}
              </div>
            )}
            
            {/* Quantity Selector */}
            <div className="space-y-3">
              <label className="text-white font-medium">Quantity:</label>
              <div className="flex items-center gap-4">
                <div className="flex items-center bg-white/5 rounded-lg border border-white/10">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-3 text-[#94A3B8] hover:text-white transition-colors"
                    disabled={quantity <= 1}
                  >
                    <Minus className="w-5 h-5" />
                  </button>
                  <span className="w-12 text-center text-white font-bold text-lg">{quantity}</span>
                  <button
                    onClick={() => setQuantity(Math.min(product.stock_quantity, quantity + 1))}
                    className="p-3 text-[#94A3B8] hover:text-white transition-colors"
                    disabled={quantity >= product.stock_quantity}
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                <span className="text-[#64748B] text-sm">
                  Max: {product.stock_quantity}
                </span>
              </div>
            </div>
            
            {/* Add to Cart Button */}
            <div className="flex gap-3">
              <Button
                onClick={addToCart}
                disabled={product.stock_quantity === 0}
                className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 py-6 text-lg font-bold disabled:opacity-50"
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Add to Cart - {(((discountedPrice || product.price) + (selectedVariant?.price_adjustment || 0)) * quantity).toFixed(0)} NOK
              </Button>
              <Button
                variant="outline"
                className="border-white/20 text-[#94A3B8] hover:text-white hover:border-white/40 py-6"
              >
                <Heart className="w-5 h-5" />
              </Button>
              <Button
                variant="outline"
                className="border-white/20 text-[#94A3B8] hover:text-white hover:border-white/40 py-6"
              >
                <Share2 className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Features */}
            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-white/10">
              <div className="text-center">
                <Truck className="w-8 h-8 mx-auto text-green-400 mb-2" />
                <p className="text-sm text-white font-medium">Fast Delivery</p>
                <p className="text-xs text-[#94A3B8]">2-5 business days</p>
              </div>
              <div className="text-center">
                <Shield className="w-8 h-8 mx-auto text-green-400 mb-2" />
                <p className="text-sm text-white font-medium">Secure Payment</p>
                <p className="text-xs text-[#94A3B8]">Stripe checkout</p>
              </div>
              <div className="text-center">
                <RotateCcw className="w-8 h-8 mx-auto text-green-400 mb-2" />
                <p className="text-sm text-white font-medium">Free Returns</p>
                <p className="text-xs text-[#94A3B8]">30 days</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Image Zoom Modal */}
      {imageZoom && product.photos && product.photos.length > 0 && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setImageZoom(false)}
        >
          <button
            onClick={() => setImageZoom(false)}
            className="absolute top-4 right-4 text-white hover:text-green-400 transition-colors"
          >
            <span className="text-2xl">✕</span>
          </button>
          
          {product.photos.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white"
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white"
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            </>
          )}
          
          <img
            src={product.photos[selectedImage]}
            alt={product.name}
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          
          {/* Thumbnail strip in zoom mode */}
          {product.photos.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {product.photos.map((photo, index) => (
                <button
                  key={index}
                  onClick={(e) => { e.stopPropagation(); setSelectedImage(index); }}
                  className={`w-16 h-16 rounded overflow-hidden border-2 ${
                    selectedImage === index ? "border-green-500" : "border-white/30"
                  }`}
                >
                  <img src={photo} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-4 mt-12">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="px-2 py-1 bg-[#0A0A0A] border border-[#23F7DD]/50 rounded">
              <span className="text-[#23F7DD] font-bold text-sm font-['Unbounded']">All</span>
            </div>
            <span className="text-sm font-bold font-['Unbounded'] text-white">Stillas</span>
            <div className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-[#23F7DD]/10 border border-[#23F7DD]/20 rounded-full">
              <span className="text-[#23F7DD] text-[10px]">⚡</span>
              <span className="text-[10px] text-[#94A3B8]">Powered by</span>
              <span className="text-[10px] text-[#23F7DD]">Doserne AS</span>
            </div>
          </Link>
          <p className="text-sm text-[#64748B]">© 2026 Doserne AS. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
